import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fullUrl, OG_IMAGE, hreflangLinks, getLocale, SITE_NAME, buildBreadcrumbSchema } from '../utils/seo';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FiMapPin, FiLoader, FiAlertTriangle, FiChevronLeft, FiChevronRight, FiCheck } from 'react-icons/fi';
import { locations as fallbackLocations, courses as fallbackCourses, therapists as fallbackTherapists } from '../data/therapists';
import { detectNearestLocation } from '../utils/geolocation';
import { useTranslation, useLocalizedData, useLanguage } from '../i18n';
import LocationCard from '../components/LocationCard';
import CourseCard from '../components/CourseCard';
import TherapistCard from '../components/TherapistCard';
import TimeSlotPicker from '../components/TimeSlotPicker';
import api from '../utils/api';
import './BookingPage.css';

function BookingPage() {
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { t } = useTranslation();
  const { localize } = useLocalizedData();
  const { meta } = useLanguage();

  const STEPS = [
    { id: 1, label: t('step_location'), icon: '📍' },
    { id: 2, label: t('step_course'), icon: '💆' },
    { id: 3, label: t('step_therapist'), icon: '👤' },
    { id: 4, label: t('step_datetime'), icon: '📅' },
    { id: 5, label: t('step_confirm'), icon: '✅' },
  ];

  const [step, setStep] = useState(1);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactNote, setContactNote] = useState('');
  const [healthConditions, setHealthConditions] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // API data with fallback
  const [locations, setLocations] = useState(fallbackLocations);
  const [courses, setCourses] = useState(fallbackCourses);
  const [therapists, setTherapists] = useState(fallbackTherapists);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locRes, courseRes, thRes] = await Promise.all([
          api.get('/locations'),
          api.get('/courses'),
          api.get('/therapists'),
        ]);
        // Transform API data to match frontend format
        const apiLocations = locRes.data.map(l => ({
          id: l.id,
          name: { ko: l.nameKo, en: l.nameEn, zh: l.nameZh, ja: l.nameJa, th: l.nameTh, fr: l.nameFr, de: l.nameDe, es: l.nameEs },
          description: { ko: l.descKo, en: l.descEn, zh: l.descZh, ja: l.descJa, th: l.descTh, fr: l.descFr, de: l.descDe, es: l.descEs },
          image: l.image, lat: l.lat, lng: l.lng,
        }));
        const apiCourses = courseRes.data.map(c => ({
          id: c.id,
          name: { ko: c.nameKo, en: c.nameEn, zh: c.nameZh, ja: c.nameJa, th: c.nameTh, fr: c.nameFr, de: c.nameDe, es: c.nameEs },
          description: { ko: c.descKo, en: c.descEn, zh: c.descZh, ja: c.descJa, th: c.descTh, fr: c.descFr, de: c.descDe, es: c.descEs },
          icon: c.icon,
          options: (c.options || []).map(o => ({ duration: o.duration, price: o.price })),
        }));
        const apiTherapists = thRes.data.map(t => ({
          id: t.id,
          name: t.name,
          nameKo: { ko: t.nameKo, en: t.nameEn, zh: t.nameZh, ja: t.nameJa, th: t.nameTh, fr: t.nameFr, de: t.nameDe, es: t.nameEs },
          bio: { ko: t.bioKo, en: t.bioEn, zh: t.bioZh, ja: t.bioJa, th: t.bioTh, fr: t.bioFr, de: t.bioDe, es: t.bioEs },
          age: t.age, experience: t.experience, location: t.locationId,
          specialties: t.specialtyIds || [], rating: t.rating, photo: t.photo,
          height: t.height, weight: t.weight,
          features: t.features ? { ko: t.featuresKo, en: t.featuresEn, zh: t.featuresZh, ja: t.featuresJa, th: t.featuresTh, fr: t.featuresFr, de: t.featuresDe, es: t.featuresEs } : null,
        }));
        setLocations(apiLocations);
        setCourses(apiCourses);
        setTherapists(apiTherapists);
      } catch (err) {
        console.log('API unavailable, using fallback data');
      }
    };
    fetchData();
  }, []);

  // Pre-select location (and therapist if provided) via navigation state
  useEffect(() => {
    if (routerLocation.state?.locationId) {
      const loc = locations.find((l) => l.id === routerLocation.state.locationId);
      if (loc) {
        setSelectedLocation(loc);
      }
    }
    if (routerLocation.state?.therapistId) {
      const th = therapists.find((t) => t.id === routerLocation.state.therapistId);
      if (th) {
        setSelectedTherapist(th);
      }
    }
  }, [routerLocation.state, locations, therapists]);

  const handleGpsDetect = async () => {
    setGpsLoading(true);
    setGpsError('');
    try {
      const result = await detectNearestLocation();
      setSelectedLocation(result.location);
      setGpsError('');
    } catch (err) {
      setGpsError(t(err.message));
    } finally {
      setGpsLoading(false);
    }
  };

  const handleCourseSelect = (courseWithOption) => {
    setSelectedCourse(courseWithOption);
  };

  const filteredTherapists = selectedLocation
    ? therapists.filter(
        (th) =>
          th.location === selectedLocation.id &&
          (!selectedCourse || th.specialties.includes(selectedCourse.id))
      )
    : [];

  const canGoNext = () => {
    switch (step) {
      case 1:
        return !!selectedLocation;
      case 2:
        return !!selectedCourse;
      case 3:
        return !!selectedTherapist;
      case 4:
        return !!selectedDate && !!selectedTime;
      case 5:
        return contactName.trim() && contactPhone.trim() && contactEmail.trim();
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (step < 5) {
      setStep(step + 1);
    } else {
      // Submit booking
      const booking = {
        location: selectedLocation,
        course: selectedCourse,
        therapist: selectedTherapist,
        date: selectedDate,
        time: selectedTime,
        contact: { name: contactName, phone: contactPhone, email: contactEmail, note: contactNote },
      };

      // Try to submit via API
      setSubmitting(true);
      try {
        const dateStr = selectedDate.toISOString().split('T')[0];
        const apiBooking = {
          locationId: selectedLocation.id,
          courseId: selectedCourse.id,
          therapistId: selectedTherapist.id,
          duration: selectedCourse.selectedOption.duration,
          price: selectedCourse.selectedOption.price,
          bookingDate: dateStr,
          bookingTime: selectedTime,
          contactName, contactPhone, contactEmail, contactNote,
          healthConditions: healthConditions.trim() || null,
        };
        const res = await api.post('/bookings/public', apiBooking);
        navigate('/confirmation', { state: { booking, apiBooking: res.data } });
      } catch (err) {
        console.log('API booking failed, using local confirmation');
        navigate('/confirmation', { state: { booking } });
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="booking-page">
      <Helmet>
        <html lang={meta.code} />
        <title>{t('seo_booking_title')}</title>
        <meta name="description" content={t('seo_booking_desc')} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={t('seo_booking_title')} />
        <meta property="og:description" content={t('seo_booking_desc')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={fullUrl('/booking')} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:locale" content={getLocale(meta.code)} />
        <link rel="canonical" href={fullUrl('/booking')} />
        {hreflangLinks('/booking').map(({ rel, hreflang, href }) => (
          <link key={hreflang} rel={rel} hrefLang={hreflang} href={href} />
        ))}
        <script type="application/ld+json">{JSON.stringify(buildBreadcrumbSchema([
          { name: t('nav_home'), url: fullUrl('/') },
          { name: t('nav_booking'), url: fullUrl('/booking') },
        ]))}</script>
      </Helmet>
      <div className="booking-container">
        {/* Progress Bar */}
        <div className="progress-bar">
          {STEPS.map((s) => (
            <div
              key={s.id}
              className={`progress-step ${step === s.id ? 'active' : ''} ${step > s.id ? 'completed' : ''}`}
              onClick={() => s.id < step && setStep(s.id)}
            >
              <div className="step-circle">
                {step > s.id ? <FiCheck /> : <span>{s.icon}</span>}
              </div>
              <span className="step-label">{s.label}</span>
            </div>
          ))}
          <div className="progress-line">
            <div
              className="progress-fill"
              style={{ width: `${((step - 1) / (STEPS.length - 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* Step Content */}
        <div className="step-content">
          {/* Step 1: Location */}
          {step === 1 && (
            <div className="step-panel">
              <h2 className="step-title">{t('booking_location_title')}</h2>
              <p className="step-desc">{t('booking_location_desc')}</p>

              <button
                className={`gps-button ${gpsLoading ? 'loading' : ''}`}
                onClick={handleGpsDetect}
                disabled={gpsLoading}
              >
                {gpsLoading ? (
                  <>
                    <FiLoader className="spin" /> {t('gps_loading')}
                  </>
                ) : (
                  <>
                    <FiMapPin /> {t('gps_detect')}
                  </>
                )}
              </button>

              {gpsError && (
                <div className="gps-error">
                  <FiAlertTriangle /> {gpsError}
                </div>
              )}

              <div className="location-grid">
                {locations.map((loc) => (
                  <LocationCard
                    key={loc.id}
                    location={loc}
                    selected={selectedLocation?.id === loc.id}
                    onClick={setSelectedLocation}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Course */}
          {step === 2 && (
            <div className="step-panel">
              <h2 className="step-title">{t('booking_course_title')}</h2>
              <p className="step-desc">{t('booking_course_desc')}</p>

              <div className="course-grid">
                {courses.map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    selected={selectedCourse?.id === course.id ? selectedCourse : null}
                    onSelect={handleCourseSelect}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Therapist */}
          {step === 3 && (
            <div className="step-panel">
              <h2 className="step-title">{t('booking_therapist_title')}</h2>
              <p className="step-desc">
                {t('booking_therapist_desc', { location: localize(selectedLocation?.name) })}
              </p>

              <div className="booking-notice">
                <div className="booking-notice-item">
                  <span>⏰</span> {t('notice_punctual')}
                </div>
                <div className="booking-notice-item warning">
                  <span>🚫</span> {t('notice_no_sexual')}
                </div>
              </div>

              {filteredTherapists.length === 0 ? (
                <div className="empty-state">
                  <p>{t('no_therapist')}</p>
                  <p>{t('no_therapist_suggest')}</p>
                </div>
              ) : (
                <div className="therapist-grid">
                  {filteredTherapists.map((th) => (
                    <TherapistCard
                      key={th.id}
                      therapist={th}
                      selected={selectedTherapist?.id === th.id}
                      onClick={setSelectedTherapist}
                      showDetails
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Date & Time */}
          {step === 4 && (
            <div className="step-panel">
              <h2 className="step-title">{t('booking_datetime_title')}</h2>
              <p className="step-desc">{t('booking_datetime_desc')}</p>

              <div className="datetime-container">
                <div className="date-section">
                  <h3 className="sub-title">{t('date_select')}</h3>
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date) => {
                      setSelectedDate(date);
                      setSelectedTime(null);
                    }}
                    minDate={new Date()}
                    inline
                    calendarClassName="custom-calendar"
                    locale={meta.dateLocale}
                  />
                </div>

                <div className="time-section">
                  <h3 className="sub-title">{t('time_select')}</h3>
                  {selectedDate ? (
                    <TimeSlotPicker
                      selectedDate={selectedDate}
                      selectedTime={selectedTime}
                      onTimeSelect={setSelectedTime}
                    />
                  ) : (
                    <p className="time-placeholder">{t('time_placeholder')}</p>
                  )}
                </div>
              </div>

              <div className="time-notice">
                <FiAlertTriangle className="notice-icon" />
                <span>{t('time_notice')}</span>
              </div>
            </div>
          )}

          {/* Step 5: Confirmation */}
          {step === 5 && (
            <div className="step-panel">
              <h2 className="step-title">{t('booking_confirm_title')}</h2>
              <p className="step-desc">{t('booking_confirm_desc')}</p>

              <div className="summary-card">
                <div className="summary-row">
                  <span className="summary-label">📍 {t('summary_location')}</span>
                  <span className="summary-value">
                    {localize(selectedLocation?.name)}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">💆 {t('summary_course')}</span>
                  <span className="summary-value">
                    {localize(selectedCourse?.name)} — {t('duration_min', { min: selectedCourse?.selectedOption?.duration })}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">💰 {t('summary_price')}</span>
                  <span className="summary-value price">
                    {selectedCourse?.selectedOption?.price?.toLocaleString()}฿
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">👤 {t('summary_therapist')}</span>
                  <span className="summary-value">
                    {localize(selectedTherapist?.nameKo)} ({selectedTherapist?.name})
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">📅 {t('summary_date')}</span>
                  <span className="summary-value">
                    {selectedDate?.toLocaleDateString(meta.dateLocale, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      weekday: 'long',
                    })}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">⏰ {t('summary_time')}</span>
                  <span className="summary-value">{selectedTime}</span>
                </div>
              </div>

              <div className="health-check-section">
                <h3 className="sub-title">{t('health_check_title')}</h3>
                <p className="health-check-desc">{t('health_check_desc')}</p>
                <div className="health-check-examples">
                  {t('health_check_examples')}
                </div>
                <textarea
                  className="health-check-input"
                  value={healthConditions}
                  onChange={(e) => setHealthConditions(e.target.value)}
                  placeholder={t('health_check_placeholder')}
                  rows={3}
                />
              </div>

              <div className="contact-form">
                <h3 className="sub-title">{t('contact_title')}</h3>
                <div className="form-group">
                  <label>{t('contact_name')} {t('required_mark')}</label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder={t('contact_name_placeholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('contact_phone')} {t('required_mark')}</label>
                  <input
                    type="tel"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder={t('contact_phone_placeholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('contact_email')} {t('required_mark')}</label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder={t('contact_email_placeholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('contact_note')}</label>
                  <textarea
                    value={contactNote}
                    onChange={(e) => setContactNote(e.target.value)}
                    placeholder={t('contact_note_placeholder')}
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="step-nav">
          {step > 1 && (
            <button className="nav-btn back-btn" onClick={handleBack}>
              <FiChevronLeft /> {t('btn_prev')}
            </button>
          )}
          <button
            className="nav-btn next-btn"
            onClick={handleNext}
            disabled={!canGoNext() || submitting}
          >
            {step === 5 ? (
              <>{t('btn_complete')} <FiCheck /></>
            ) : (
              <>{t('btn_next')} <FiChevronRight /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BookingPage;
